import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpBackend,
  HttpEvent,
  HttpResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { Params } from '@angular/router';
import { IOnboardingKYC, IOnboardingParamsWithoutFormat } from '../../core/models/onboarding.model';
import { map } from 'rxjs/operators';
import {
  IOnboardingSession,
  IOnboardingParams,
  IKycApplication,
} from '../../core/models/onboarding.model';
import { environment } from '@environment';
import { ServerMock } from '../mocks/app-serve.mock';
import { REQUEST_MOCK_KYC } from '../mocks/app-control.mock';
import { HandleTokenService } from '../../core/services/handle-token.service';
import { URLS } from './url.constants';
import { UtilService } from '@app/services/util.service';
import { CIVIL_STATUS_VALUES } from '@app/core/enums/onboarding.enum';

const API_URL = environment.api_url;

@Injectable({
  providedIn: 'root',
})
export class OnboardingAPI {
  public _httpClient = new HttpClient(this._httpBackend);
  private _localServer: ServerMock = new ServerMock();

  constructor(
    private _http: HttpClient,
    public _httpBackend: HttpBackend,
    private _handleTokenService: HandleTokenService,
    private _utilService: UtilService
  ) {}

  public requestOnboardingSession(step: number): Observable<IOnboardingSession> {
    return this._http.get<IOnboardingSession>(`/onboarding/step/${step}/`);
  }

  public saveOnboardingStep(step: number, stepInfo: IOnboardingParams | {}): Observable<{}> {
    return this._http.post<{}>(`/onboarding/step/${step}/`, stepInfo);
  }

  public savePreCustomer(stepInfo: IOnboardingParams): Observable<{}> {
    const data = {
      first_name: stepInfo.firstName,
      middle_name: stepInfo.middleName,
      first_surname: stepInfo.firstSurname,
      second_surname: stepInfo.secondSurname,
      identification_number: stepInfo.identificationNumber,
      identification_expedition_at: this._utilService.changeDateToISOString(
        stepInfo.identificationExpeditionAt
      ),
      occupation: stepInfo.occupation,
      email: stepInfo.email,
      email_confirmation: stepInfo.emailConfirmation,
      accept_processing_of_personal_data: stepInfo.acceptProcessingOfPersonalData,
      accept_personal_data_for_commercial_purposes:
        stepInfo.acceptPersonalDataForCommercialPurposes,
      civil_status: stepInfo.civilStatus,
    };
    if (stepInfo.civilStatus === CIVIL_STATUS_VALUES.MARRIED) {
      data['spouse_name'] = stepInfo.spouseName;
      data['spouse_surname'] = stepInfo.spouseSurname;
    }
    return this._http.patch<{}>(`${URLS.PRE_CUSTOMER}`, data);
  }

  public getPreCustomer(): Observable<IOnboardingParamsWithoutFormat> {
    return this._http.get<IOnboardingParamsWithoutFormat>(`${URLS.PRE_CUSTOMER}`);
  }

  public upgradeCustomer(): Observable<{}> {
    return this._http.post<{}>(`${URLS.UPGRADE_CUSTOMER}`, {});
  }

  public getOnboardingStep(step: number): Observable<IOnboardingSession> {
    return this._http.get<IOnboardingSession>(`/onboarding/step/${step}/`);
  }

  public uploadDocumentStatus(application: IKycApplication | Params): Observable<{}> {
    return this._http.post('/kyc/application/', application);
  }

  public requestKYCStatus(step: number): Observable<IOnboardingKYC | HttpEvent<any>> {
    const { production, isTestServer } = environment;
    const httpRequest: any = REQUEST_MOCK_KYC;
    const token = this._handleTokenService.getAccessToken();
    const options = {
      headers: this._setHeaders(token),
    };

    return production && !isTestServer
      ? this._httpClient
          .get<IOnboardingSession>(`${API_URL}/onboarding/step/${step}/`, options)
          .pipe(
            map((information) => {
              const { data } = information;
              return data;
            })
          )
      : this._localServer.request(httpRequest).pipe(
          map((response: HttpResponse<any>) => {
            const session: IOnboardingSession = response?.body;
            const { data } = session;
            return data;
          })
        );
  }

  private _setHeaders(transactionToken):
    | HttpHeaders
    | {
        [header: string]: string | string[];
      } {
    return {
      'Content-Type': 'application/json',
      Authorization: `Token: ${transactionToken}`,
    };
  }
}
